import React, { useEffect, useRef, useState } from "react";
import { FaRegStopCircle } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { IoIosSend } from "react-icons/io";
import { useHistory } from "react-router-dom";

const API_BASE = "https://204.168.149.189/v1";
const AUTH_TOKEN = "Bearer app-RwAMNt12y2YAKTZhyZNBUN0E";

export function ChatInterface({ credits, onSessionCreated }) {
    const navigate = useHistory();
    const user = JSON.parse(sessionStorage.getItem("app_user"));

    const [previewUrl, setPreviewUrl] = useState(null);
    const [file, setFile] = useState(null);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [taskId, setTaskId] = useState(null); // ✅ track task_id for stop
    const [conversationId, setConversationId] = useState(""); // ✅ track current conversation
    const [messages, setMessages] = useState([
        {
            id: "welcome",
            from: "ai",
            text: `Hi ${user?.first_name || user?.user_name}, how can I help you today?`,
            timestamp: new Date(),
        },
    ]);

    const messageContainer = useRef();

    const params = new URLSearchParams(window.location.search);
    const userId = params.get("user_id") || params.get("userid");
    const sessionFromUrl = params.get("session_id");

    const scrollToBottom = () => {
        if (messageContainer.current) {
            messageContainer.current.scrollTop = messageContainer.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // ✅ Load existing session messages on mount
    useEffect(() => {
        if (!userId) return;

        // Normalize URL param
        if (params.get("userid")) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.set("user_id", userId);
            newUrl.searchParams.delete("userid");
            window.history.replaceState({}, "", newUrl);
        }

        const isNew = params.get("new") === "1";

        if (sessionFromUrl && !isNew) {
            setConversationId(sessionFromUrl);
            fetchMessages(sessionFromUrl);
        } else if (!sessionFromUrl) {
            // No session yet — start fresh
            setConversationId("");
        }
    }, [userId]);

    // ✅ Fixed fetchMessages — uses response data not local state
    const fetchMessages = async (sessionId) => {
        try {
            const res = await fetch(
                `${API_BASE}/messages?user=${userId}&conversation_id=${sessionId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: AUTH_TOKEN,
                    },
                }
            );
            const response = await res.json();
            const data = response?.data || [];

            const formatted = [];
            data.forEach((msg) => {
                // Skip default greeting
                if (msg.answer === "Hello! How can I help you today?") return;

                // User message
                if (msg.query) {
                    formatted.push({
                        id: `${msg.id}-user`,
                        from: "user",
                        text: msg.query,
                        file_url: msg.message_files?.[0]?.url || null,
                        timestamp: new Date(msg.created_at * 1000),
                    });
                }

                // AI message
                if (msg.answer) {
                    formatted.push({
                        id: `${msg.id}-ai`,
                        from: "ai",
                        text: msg.answer.replace(/<think>[\s\S]*?<\/think>/g, "").trim(),
                        timestamp: new Date(msg.created_at * 1000),
                    });
                }
            });

            if (formatted.length > 0) {
                setMessages([
                    {
                        id: "welcome",
                        from: "ai",
                        text: `Hi ${user?.first_name || user?.user_name}, how can I help you today?`,
                    },
                    ...formatted,
                ]);
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    // ✅ Typing animation
    const typeText = async (fullText, delay = 20) => {
        return new Promise((resolve) => {
            let currentText = "";
            let i = 0;
            const interval = setInterval(() => {
                currentText += fullText[i];
                setMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        text: currentText,
                    };
                    return updated;
                });
                i++;
                scrollToBottom();
                if (i >= fullText.length) {
                    clearInterval(interval);
                    resolve();
                }
            }, delay);
        });
    };

    // ✅ Fixed handleSend — uses conversationId state, not empty string
    const handleSend = async () => {
        if (input.trim() === "" && !file) return;

        let localFileData = null;
        if (file) {
            localFileData = {
                file_url: URL.createObjectURL(file),
                fileType: file.type,
                fileName: file.name,
            };
        }

        const userMessage = {
            id: `user-${Date.now()}`,
            from: "user",
            text: input,
            ...localFileData,
        };

        setMessages((prev) => [...prev, userMessage]);
        scrollToBottom();

        const currentInput = input;
        setInput("");
        setPreviewUrl(null);
        setFile(null);
        setIsLoading(true);

        const payload = {
            inputs: {},
            user: userId,
            conversation_id: conversationId, // ✅ use tracked conversation
            query: currentInput,
            response_mode: "blocking",
            files: [],
        };

        try {
            const response = await fetch(`${API_BASE}/chat-messages`, {
                method: "POST",
                body: JSON.stringify(payload),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
            });

            const res = await response.json();
            const newConversationId = res?.conversation_id;

            // ✅ Track task_id for stop functionality
            if (res?.task_id) setTaskId(res.task_id);

            // ✅ Update conversation ID + URL
            if (newConversationId && newConversationId !== conversationId) {
                setConversationId(newConversationId);
                const url = new URL(window.location);
                url.searchParams.set("session_id", newConversationId);
                url.searchParams.delete("new");
                window.history.replaceState({}, "", url);

                // Notify sidebar to refresh sessions
                onSessionCreated?.();
            }

            let aiText = (res?.answer || "No response from AI")
                .replace(/<think>[\s\S]*?<\/think>/g, "")
                .trim();

            // Add placeholder then animate
            setMessages((prev) => [
                ...prev,
                { id: `ai-${Date.now()}`, from: "ai", text: "" },
            ]);

            await typeText(aiText);
        } catch (error) {
            console.error("Error sending message:", error);
            setMessages((prev) => [
                ...prev,
                {
                    id: `ai-error-${Date.now()}`,
                    from: "ai",
                    text: "Sorry, something went wrong. Please try again.",
                },
            ]);
        } finally {
            setIsLoading(false);
            setTaskId(null);
        }
    };

    // ✅ Fixed handleStop — uses tracked taskId
    const handleStop = async () => {
        if (!taskId) return;
        try {
            await fetch(`${API_BASE}/chat-messages/${taskId}/stop`, {
                method: "POST",
                body: JSON.stringify({ user: userId }),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
            });
        } catch (e) {
            console.error("Stop error:", e);
        } finally {
            setIsLoading(false);
            setTaskId(null);
        }
    };

    const handleFileUpload = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        setFile(selectedFile);
        if (selectedFile.type.startsWith("image/")) {
            setPreviewUrl(URL.createObjectURL(selectedFile));
        } else {
            setPreviewUrl(null);
        }
    };

    const handleQuickAction = (action) => {
        const prompts = {
            "Assignment help": "I need help with my assignment. Can you guide me through it?",
            "Exam preparation": "Help me prepare for my upcoming exam. What should I focus on?",
            "Summarize my lecture": "Can you help me summarize my lecture notes?",
            "Research": "I need help with research for my topic. Where should I start?",
        };
        setInput(prompts[action] || action);
    };

    return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", maxWidth: "768px", margin: "0 auto", width: "100%" }}>

                {/* Welcome Section — only when no conversation yet */}
                {messages.length === 1 && (
                    <div style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
                        <div style={{
                            width: "4rem", height: "4rem", borderRadius: "9999px",
                            backgroundColor: "#CC6828", display: "flex", alignItems: "center",
                            justifyContent: "center", margin: "0 auto 1.5rem",
                        }}>
                            <span role="img" aria-label="bot" style={{ fontSize: "1.5rem", color: "white" }}>🤖</span>
                        </div>
                        <h1 style={{ fontSize: "1.875rem", fontWeight: "bold", marginBottom: "1rem" }}>
                            Hey, I'm your AI tutor, Clara!
                        </h1>
                        <p style={{ color: "#6c757d", maxWidth: "36rem", margin: "0 auto 0.5rem" }}>
                            I am trained with high-quality academic resources to provide accurate responses.
                        </p>
                        <p style={{ color: "#6c757d", maxWidth: "36rem", margin: "0 auto 1.5rem" }}>
                            I adapt to your course and offer personalized guidance to help you ace your exams!
                        </p>
                        <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginBottom: "2rem" }}>
                            <span style={{ backgroundColor: "#e0e0e0", borderRadius: "9999px", padding: "0.25rem 0.75rem", fontSize: "0.875rem" }}>C</span>
                            <span style={{ backgroundColor: "#e0e0e0", borderRadius: "9999px", padding: "0.25rem 0.75rem", fontSize: "0.875rem" }}>{credits} credits</span>
                        </div>
                        <div style={{ maxWidth: "36rem", margin: "0 auto" }}>
                            <p style={{ fontSize: "0.875rem", color: "#6c757d", marginBottom: "1rem" }}>What can I help you with today?</p>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.75rem" }}>
                                {["Assignment help", "Exam preparation", "Summarize my lecture", "Research"].map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => handleQuickAction(option)}
                                        style={{ padding: "1rem", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "0.5rem", cursor: "pointer", fontSize: "0.875rem" }}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Messages + Input */}
                <div style={{ display: "flex", flexDirection: "column", height: messages.length === 1 ? "20vh" : "90vh" }}>

                    {/* Scrollable messages */}
                    <div
                        ref={messageContainer}
                        style={{ flex: 1, overflowY: "auto", padding: "1rem 1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}
                    >
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                style={{ display: "flex", justifyContent: message.from === "user" ? "flex-end" : "flex-start" }}
                            >
                                <div style={{
                                    maxWidth: "24rem",
                                    padding: "0.75rem 1rem",
                                    borderRadius: "0.5rem",
                                    backgroundColor: message.from === "user" ? "#CC6828" : "#e5e5ea",
                                    color: message.from === "user" ? "#fff" : "#000",
                                    marginLeft: message.from === "user" ? "3rem" : 0,
                                    marginRight: message.from === "user" ? 0 : "3rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.5rem",
                                    whiteSpace: "pre-wrap",
                                }}>
                                    {/* File preview */}
                                    {message.file_url && /\.(png|jpe?g|gif|webp)$/i.test(message.file_url) ? (
                                        <img src={message.file_url} alt="uploaded" style={{ width: "100%", borderRadius: "0.375rem" }} />
                                    ) : message.file_url ? (
                                        <a href={message.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                            📎 {message.fileName || "View File"}
                                        </a>
                                    ) : null}
                                    {message.text}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Loading indicator */}
                    {isLoading && (
                        <p className="mt-2 ml-4 text-sm text-primary font-bold">
                            Generating response{" "}
                            <span className="loading-dots">
                                <span>.</span><span>.</span><span>.</span>
                            </span>
                        </p>
                    )}

                    {/* Input area */}
                    <div style={{ borderTop: "1px solid #dee2e6", backgroundColor: "#f8f9fa", padding: "1rem" }}>
                        {/* File preview */}
                        {file && (
                            <div className="p-2 rounded-md bg-gray-100 mb-2">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Preview" className="max-h-48 object-contain rounded" />
                                ) : (
                                    <p className="text-sm text-gray-700">📎 <span className="font-medium">{file.name}</span></p>
                                )}
                            </div>
                        )}

                        <div style={{ maxWidth: "768px", margin: "0 auto", display: "flex", gap: "0.5rem" }}>
                            <input
                                type="text"
                                placeholder="Ask anything"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSend()}
                                style={{ flex: 1, padding: "0.75rem", border: "1px solid #ccc", borderRadius: "0.375rem" }}
                            />

                            <label className="inline-flex items-center justify-center px-3 rounded-md bg-gray-200 hover:bg-gray-300 cursor-pointer">
                                <FiUpload className="text-gray-700" />
                                <input type="file" className="hidden" onChange={handleFileUpload} />
                            </label>

                            {!isLoading ? (
                                <button
                                    onClick={handleSend}
                                    disabled={input.trim() === "" && !file}
                                    style={{ padding: "0 1rem", backgroundColor: "#CC6828", color: "white", border: "none", borderRadius: "0.375rem", cursor: "pointer" }}
                                >
                                    <IoIosSend />
                                </button>
                            ) : (
                                <button
                                    onClick={handleStop}
                                    style={{ padding: "0 1rem", backgroundColor: "#CC6828", color: "white", border: "none", borderRadius: "0.375rem", cursor: "pointer" }}
                                >
                                    <FaRegStopCircle />
                                </button>
                            )}
                        </div>

                        <p style={{ fontSize: "0.75rem", color: "#6c757d", textAlign: "center", marginTop: "0.5rem" }}>
                            StudyCrowd.AI can make mistakes. Please check important info.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}